import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Stack,
  Typography,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Mail,
  Edit,
  Ban,
  Unlock,
  CheckCircle2,
  Sparkles,
  Briefcase,
  TrendingUp,
  FileText,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CustomPagination from "views/client/pagination";
import EditRecruiterDialog from './EditRecruiterDialog';
import RecruiterDetailsDialog from './RecruiterDetailsDialog';

const colors = {
  primary: '#0017E7',
  secondary: '#F4E1F7',
  success: '#10b981',
  error: '#ef4444',
  text: '#1e293b',
  lightText: '#64748b',
};

const RecruiterCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 20,
  border: `2px solid ${colors.secondary}`,
  boxShadow: '0 4px 20px rgba(0, 23, 231, 0.08)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: '#ffffff',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 50%, ${colors.primary} 100%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 3s ease infinite',
  },
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 32px rgba(0, 23, 231, 0.15)`,
    borderColor: colors.primary,
  },
}));

const StyledChip = styled(Chip)(({ chipcolor }) => ({
  fontWeight: 700,
  fontSize: '10px',
  letterSpacing: '0.5px',
  height: 22,
  borderRadius: 8,
  background: chipcolor === 'success' 
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  color: 'white',
  border: 'none',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const StatItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  borderRadius: 10,
  background: `${colors.secondary}40`,
  border: `1px solid ${colors.secondary}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    background: colors.secondary,
  },
});

const ActionButton = styled(IconButton)(({ btncolor }) => ({
  width: 36,
  height: 36,
  backgroundColor: btncolor === 'primary' 
    ? colors.secondary
    : btncolor === 'success'
    ? '#d1fae5'
    : '#fee2e2',
  color: btncolor === 'primary' 
    ? colors.primary
    : btncolor === 'success'
    ? colors.success
    : colors.error,
  border: `1.5px solid ${btncolor === 'primary' ? colors.primary : btncolor === 'success' ? colors.success : colors.error}30`,
  borderRadius: 10,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: btncolor === 'primary' 
      ? colors.primary
      : btncolor === 'success'
      ? colors.success
      : colors.error,
    color: 'white',
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${btncolor === 'primary' ? colors.primary : btncolor === 'success' ? colors.success : colors.error}40`,
  },
}));

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return first + last || 'U';
};

const CompanyRecruitersCard = ({
  recruiters,
  totalRecruiters,
  page,
  rowsPerPage,
  handleChangePage,
  handleRowsPerPageChange,
  onRecruiterUpdated
}) => {
  const { t } = useTranslation();

  const getStatus = (isActive) => {
    if (!isActive) return { label: t('superAdmin.companyRecruitersCard.inactive'), color: 'error' };
    return { label: t('superAdmin.companyRecruitersCard.active'), color: 'success' };
  };
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, item: null, action: null });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, item: null });
  const [localRecruiters, setLocalRecruiters] = useState(recruiters);

  // Update local state when props change
  React.useEffect(() => {
    setLocalRecruiters(recruiters);
  }, [recruiters]);

  const handleEdit = (recruiter) => {
    setEditDialog({ open: true, item: recruiter });
  };

  const handleViewDetails = (recruiter) => {
    setDetailsDialog({ open: true, item: recruiter });
  };

  const handleCardClick = (recruiter) => {
    handleViewDetails(recruiter);
  };

  const handleBlockUnblockClick = (recruiter) => {
    const action = recruiter.is_active ? 'block' : 'unblock';
    setConfirmDialog({ open: true, item: recruiter, action });
  };

  const handleBlockUnblock = async () => {
    const recruiter = confirmDialog.item;
    try {
      const token = localStorage.getItem('accessToken');
      const base = process.env.REACT_APP_API_URL || '';
      const endpoint = `${base}recruiters/${recruiter.id}/update/`;
      const newStatus = !recruiter.is_active;

      const res = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      if (!res.ok) {
        throw new Error('Failed to update recruiter status');
      }

      const updated = await res.json();
      // Update the local state to immediately reflect the change
      setLocalRecruiters(prev => prev.map(r =>
        r.id === recruiter.id ? updated : r
      ));
      onRecruiterUpdated && onRecruiterUpdated(updated);
      setConfirmDialog({ open: false, item: null, action: null });
    } catch (err) {
      console.error('Error updating recruiter status:', err);
    }
  };

  const handleRecruiterUpdated = (updatedRecruiter) => {
    // Update the local state to immediately reflect the change
    setLocalRecruiters(prev => prev.map(r =>
      r.id === updatedRecruiter.id ? updatedRecruiter : r
    ));
    onRecruiterUpdated && onRecruiterUpdated(updatedRecruiter);
    setEditDialog({ open: false, item: null });
  };

  const handleDetailsClose = () => {
    setDetailsDialog({ open: false, item: null });
  };

  return (
    <>
      <Grid container spacing={3}>
        {localRecruiters.map((recruiter) => {
          const status = getStatus(recruiter.is_active);
          return (
            <Grid item xs={12} sm={6} lg={4} key={recruiter.id}>
              <RecruiterCard onClick={() => handleCardClick(recruiter)} style={{ cursor: 'pointer' }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  {/* Header Section */}
                  <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="flex-start">
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={recruiter.image}
                        sx={{
                          width: 48,
                          height: 48,
                          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                          fontSize: '18px',
                          fontWeight: 800,
                          border: `2px solid ${colors.secondary}`,
                        }}
                      >
                        {getInitials(recruiter.first_name, recruiter.last_name)}
                      </Avatar>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: -3,
                          right: -3,
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${colors.primary} 0%, #6366f1 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                        }}
                      >
                        <Sparkles size={9} color="white" />
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          mb: 0.25,
                          color: colors.text,
                          lineHeight: 1.3,
                        }}
                      >
                        {recruiter.first_name} {recruiter.last_name}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" sx={{ color: colors.lightText, fontWeight: 600 }}>
                          @{recruiter.username}
                        </Typography>
                        <CheckCircle2 size={12} color={colors.primary} fill={colors.primary} />
                      </Stack>
                    </Box>

                    <StyledChip
                      label={status.label.toUpperCase()}
                      chipcolor={status.color}
                      size="small"
                    />
                  </Stack>

                  {/* Email Section */}
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.25,
                      borderRadius: 2,
                      background: `${colors.secondary}30`,
                      border: `1px solid ${colors.secondary}80`,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.25,
                          background: `linear-gradient(135deg, ${colors.primary} 0%, #6366f1 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Mail size={14} color="white" />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                          flex: 1,
                        }}
                        title={recruiter.email}
                      >
                        {recruiter.email}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Stats Section - Horizontal Layout */}
                  <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'space-between' }}>
                    <StatItem sx={{ flex: '1 1 0', minWidth: 0 }}>
                      <Briefcase size={16} color={colors.primary} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: colors.text, lineHeight: 1 }}>
                          {recruiter.posts_count}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.lightText, fontWeight: 600, fontSize: '9px' }}>
                          {t('superAdmin.companyRecruitersCard.posts')}
                        </Typography>
                      </Box>
                    </StatItem>

                    <StatItem sx={{ flex: '1 1 0', minWidth: 0, background: '#d1fae540', borderColor: '#10b98140' }}>
                      <TrendingUp size={16} color={colors.success} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: colors.success, lineHeight: 1 }}>
                          {recruiter.active_posts}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.success, fontWeight: 600, fontSize: '9px' }}>
                          {t('superAdmin.companyRecruitersCard.active')}
                        </Typography>
                      </Box>
                    </StatItem>

                    <StatItem sx={{ flex: '1 1 0', minWidth: 0 }}>
                      <FileText size={16} color={colors.primary} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: colors.primary, lineHeight: 1 }}>
                          {recruiter.applications_count}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.primary, fontWeight: 600, fontSize: '9px' }}>
                          {t('superAdmin.companyRecruitersCard.apps')}
                        </Typography>
                      </Box>
                    </StatItem>
                  </Stack>

                  {/* Actions Section */}
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'center',
                      pt: 1.5,
                      borderTop: `1px solid ${colors.secondary}80`,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionButton btncolor="success" onClick={() => handleEdit(recruiter)}>
                      <Edit size={18} />
                    </ActionButton>
                    {status.label === 'active' ? (
                      <ActionButton btncolor="error" onClick={() => handleBlockUnblockClick(recruiter)}>
                        <Ban size={18} />
                      </ActionButton>
                    ) : (
                      <ActionButton
                        btncolor="error"
                        onClick={() => handleBlockUnblockClick(recruiter)}
                        sx={{
                          backgroundColor: '#fee2e2',
                          color: colors.error,
                          border: `1.5px solid ${colors.error}30`,
                          '&:hover': {
                            background: colors.error,
                            color: 'white',
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${colors.error}40`,
                          },
                        }}
                      >
                        <Unlock size={18} />
                      </ActionButton>
                    )}
                  </Stack>
                </CardContent>
              </RecruiterCard>
            </Grid>
          );
        })}
      </Grid>

      {totalRecruiters > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CustomPagination
            page={page}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            count={totalRecruiters}
          />
        </Box>
      )}

      <EditRecruiterDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, item: null })}
        item={editDialog.item}
        onUpdated={handleRecruiterUpdated}
      />

      <RecruiterDetailsDialog
        open={detailsDialog.open}
        onClose={handleDetailsClose}
        recruiter={detailsDialog.item}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, item: null, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'block' ? t('superAdmin.companyRecruitersCard.blockRecruiter') : t('superAdmin.companyRecruitersCard.unblockRecruiter')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'block'
              ? t('superAdmin.companyRecruitersCard.blockMessage', { name: `${confirmDialog.item?.first_name} ${confirmDialog.item?.last_name}` })
              : t('superAdmin.companyRecruitersCard.unblockMessage', { name: `${confirmDialog.item?.first_name} ${confirmDialog.item?.last_name}` })
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, item: null, action: null })}
            color="inherit"
          >
            {t('superAdmin.companyRecruitersCard.cancel')}
          </Button>
          <Button
            onClick={handleBlockUnblock}
            color={confirmDialog.action === 'block' ? 'error' : 'success'}
            variant="contained"
          >
            {confirmDialog.action === 'block' ? t('superAdmin.companyRecruitersCard.block') : t('superAdmin.companyRecruitersCard.unblock')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyRecruitersCard;